import { Box, Flex, Grid, Text, VStack } from '@chakra-ui/react'
import { motion } from 'framer-motion'
import { useNavigate, useParams } from 'react-router-dom'
import { LuChevronRight as ChevronRight } from 'react-icons/lu'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts'
import { useChartTheme } from '../../../hooks/useChartTheme'
import { brand, themeColors, statusColors } from '../../../theme'
import { supplierDetails } from '../../../data/mockEstadisticas'
import CustomTooltip from '../components/CustomTooltip'
import ChartCard from '../components/ChartCard'
import TrendIndicator from '../components/TrendIndicator'

const MotionBox = motion.create(Box)

const estadoColors = {
  completado: { bg: `${statusColors.success}33`, text: statusColors.success },
  en_transito: { bg: `${brand.green}33`, text: brand.green },
  pendiente: { bg: `${statusColors.info}33`, text: statusColors.info },
}

export default function DetalleProveedorPage() {
  const { proveedorId } = useParams()
  const { axis, grid } = useChartTheme()
  const colors = themeColors
  const navigate = useNavigate()

  const detail = supplierDetails.find((s) => s.id === proveedorId)
  if (!detail) {
    return (
      <Text color={colors.text} fontSize="lg">
        Proveedor no encontrado
      </Text>
    )
  }

  return (
    <VStack gap={6} alignItems="stretch">
      {/* Breadcrumb */}
      <MotionBox initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <Flex gap={2} alignItems="center" mb={2}>
          <Text fontSize="sm" color={brand.green} cursor="pointer" _hover={{ textDecoration: 'underline' }} onClick={() => navigate('/app/estadisticas')}>
            Estadisticas
          </Text>
          <ChevronRight size={14} color={colors.textMuted} />
          <Text fontSize="sm" color={brand.green} cursor="pointer" _hover={{ textDecoration: 'underline' }} onClick={() => navigate('/app/estadisticas/proveedores')}>
            Proveedores
          </Text>
          <ChevronRight size={14} color={colors.textMuted} />
          <Text fontSize="sm" color={colors.text} fontWeight="600">
            {detail.nombre}
          </Text>
        </Flex>
        <Text fontSize="2xl" fontWeight="bold" color={colors.text}>
          {detail.nombre}
        </Text>
        <Text fontSize="xs" color={colors.textMuted}>
          RUC: {detail.ruc} — Mineral principal: {detail.mineralPrincipal}
        </Text>
      </MotionBox>

      {/* KPIs */}
      <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={4}>
        {[
          { label: 'Tonelaje Total', value: `${detail.tonelajeTotal.toLocaleString()} TM`, trend: 8.5, dir: 'up' as const },
          { label: 'Traslados Totales', value: detail.trasladosTotales.toString(), trend: 5.2, dir: 'up' as const },
          { label: 'Tasa de Entrega', value: `${detail.tasaEntrega.toFixed(1)}%`, trend: 1.8, dir: 'up' as const },
        ].map((kpi, i) => (
          <MotionBox
            key={kpi.label}
            bg={colors.bgCard}
            border="1px solid"
            borderColor={colors.border}
            borderRadius="2xl"
            p={5}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: i * 0.05 }}
            boxShadow="none"
          >
            <Text fontSize="10px" textTransform="uppercase" letterSpacing="0.2em" fontWeight="bold" color={colors.textMuted} mb={2}>
              {kpi.label}
            </Text>
            <Text fontSize="xl" fontWeight="bold" color={colors.text} mb={1}>
              {kpi.value}
            </Text>
            <TrendIndicator value={kpi.trend} direction={kpi.dir} />
          </MotionBox>
        ))}
      </Grid>

      {/* Charts */}
      <Grid templateColumns={{ base: '1fr', lg: '2fr 1fr' }} gap={6}>
        <ChartCard title="Tonelaje Mensual" subtitle="Evolucion del proveedor" delay={0.2}>
          <Box h="280px">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={detail.mensual}>
                <CartesianGrid stroke={grid.stroke} strokeDasharray={grid.strokeDasharray} />
                <XAxis dataKey="month" stroke={axis.stroke} tick={axis.tick} />
                <YAxis stroke={axis.stroke} tick={axis.tick} />
                <Tooltip content={<CustomTooltip formatter={(v) => `${v.toLocaleString()} TM`} />} />
                <Line type="monotone" dataKey="tonelaje" stroke={brand.green} strokeWidth={2.5} dot={{ fill: brand.green, r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </ChartCard>
        <ChartCard title="Distribucion de Minerales" delay={0.25}>
          <Box h="280px">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={detail.minerales} cx="50%" cy="50%" innerRadius="50%" outerRadius="75%" dataKey="tonelaje" nameKey="tipo" paddingAngle={3} strokeWidth={0}>
                  {detail.minerales.map((m) => (
                    <Cell key={m.tipo} fill={m.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip formatter={(v) => `${v.toLocaleString()} TM`} />} />
              </PieChart>
            </ResponsiveContainer>
          </Box>
        </ChartCard>
      </Grid>

      {/* Recent Transfers Table */}
      <MotionBox
        bg={colors.bgCard}
        border="1px solid"
        borderColor={colors.border}
        borderRadius="2xl"
        overflow="hidden"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.3 }}
        boxShadow="none"
      >
        <Box p={6} borderBottom="1px solid" borderColor={colors.border}>
          <Text fontSize="lg" fontWeight="bold" color={colors.text}>
            Traslados Recientes
          </Text>
        </Box>
        <Box overflowX="auto">
          <Box as="table" width="100%" textAlign="left">
            <Box as="thead">
              <Box as="tr" bg={colors.bgHover}>
                {['ID', 'Fecha', 'Mineral', 'Tonelaje', 'Destino', 'Estado'].map((h) => (
                  <Box as="th" key={h} px={6} py={4} fontSize="10px" textTransform="uppercase" letterSpacing="0.2em" fontWeight="bold" color={colors.textMuted}>
                    {h}
                  </Box>
                ))}
              </Box>
            </Box>
            <Box as="tbody">
              {detail.trasladosRecientes.map((t) => {
                const badge = estadoColors[t.estado]
                return (
                  <Box as="tr" key={t.id} borderBottom="1px solid" borderColor={colors.border} _hover={{ bg: colors.bgHover }}>
                    <Box as="td" px={6} py={4}><Text fontFamily="monospace" fontSize="sm" color={colors.text}>{t.id}</Text></Box>
                    <Box as="td" px={6} py={4}><Text fontSize="sm" color={colors.text}>{t.fecha}</Text></Box>
                    <Box as="td" px={6} py={4}><Text fontSize="sm" fontWeight="500" color={brand.green}>{t.mineral}</Text></Box>
                    <Box as="td" px={6} py={4}><Text fontSize="sm" color={colors.text}>{t.tonelaje} TM</Text></Box>
                    <Box as="td" px={6} py={4}><Text fontSize="sm" color={colors.text}>{t.destino}</Text></Box>
                    <Box as="td" px={6} py={4}>
                      <Box display="inline-block" px={2} py={1} borderRadius="md" fontSize="10px" fontWeight="bold" textTransform="uppercase" bg={badge.bg} color={badge.text}>
                        {t.estado.replace('_', ' ')}
                      </Box>
                    </Box>
                  </Box>
                )
              })}
            </Box>
          </Box>
        </Box>
      </MotionBox>
    </VStack>
  )
}
